import ProceduralCanvasTexture from "../procedural-canvas-texture";
import MaterialBuilder from "../../../rendering/material-builder";
import AssetLibrary from "../../../assets/asset-library";
import GeometryTetrahedron from "./geo-tetrahedron";

class GeometryBase {
    /**
     * @type {THREE.BufferGeometry[]}
     */
    _geometries = [];
    /**
     * @type {ProceduralCanvasTexture[]}
     */
    _materials = [];
    /**
     * @type {string[]}
     */
    _texts = [];

    _color = "#ffffff";
    _edgeColor = "#000000";
    _textColor = "#000000";
    _font = "Arial";
    _fontSize = 64;

    constructor(canvasSize) {
        this._createFaceNormals();
        this._build(canvasSize);
        this._redrawTexts();
    }

    _createFaceNormals() {
        if (this.normals) return;

        const sides = this.sides;

        const v0 = new THREE.Vector3(), v1 = new THREE.Vector3();
        const tmpNormal = new THREE.Vector3();

        const arrIndices = this.indices;
        const arrVertices = this.vertices;
        const arrNormals = new Array(sides).fill(1).map(() => new THREE.Vector3());

        for (let i = 0; i < sides; i++) {
            const indices = arrIndices[i].flat();
            const normal = arrNormals[i];

            for (let j = 0, len = indices.length; j < len; j += 3) {
                const a = indices[j + 0], b = indices[j + 1], c = indices[j + 2];
                const x0 = arrVertices[a], x1 = arrVertices[b], x2 = arrVertices[c];

                v0.subVectors(x0, x2);
                v1.subVectors(x1, x2);

                tmpNormal.crossVectors(v0, v1);
                normal.add(tmpNormal);
            }

            normal.divideScalar(indices.length / 3).normalize();
        }

        this.constructor.normals = arrNormals;
    }

    _dispose() {
        this._geometries.forEach(x => x.dispose());
        this._materials.forEach(x => x.dispose());
    }

    getSideInfo(index) {
        return {
            material: this._materials[index],
            geometry: this._geometries[index],
            normal: new THREE.Vector3().copy(this.normals[index])
        };
    }

    get texts() { return this._texts; }

    get editable() { return this.constructor.editable; }
    get indices() { return this.constructor.indices; }
    get indicesUv() { return this.constructor.indicesUv; }
    get pairs() { return this.constructor.pairs; }
    get additionalPairs() { return this.constructor.additionalPairs; }
    get flipOrder() { return this.constructor.flipOrder; }
    get vertices() { return this.constructor.vertices; }
    get sides() { return this.indices.length; }
    get normals() { return this.constructor.normals; }

    get attrIndices() { return this.constructor.attrIndices; }
    get attrUv() { return this.constructor.attrUv; }

    _redrawTexts(...index) {
        const sides = this.sides;

        if (index.length === 0) index = new Array(this.sides).fill(1).map((_, i) => i);

        const indicesUv = this.indicesUv !== undefined ? this.indicesUv : new Array(sides).fill(0);
        const pairs = this.indicesUv !== undefined ? this.pairs : [this.pairs];
        const additionalPairs = this.indicesUv !== undefined ? this.additionalPairs : [this.additionalPairs];

        index.forEach(index => {
            /**
             * @type {ProceduralCanvasTexture}
             */
            const texture = this._materials[index].map;
            const pair = indicesUv[index];
            const segments = pairs[pair];
            const additionalLines = additionalPairs[pair];

            texture.renderText({
                color: this._color,
                edgeColor: this._edgeColor,
                textColor: this._textColor,
                font: this._font,
                fontSize: this._fontSize,
                text: this._texts[index],
                segments, additionalLines
            });
        });
    }

    getText(index) { return this._texts[index]; }
    setText(index, value) {
        this._texts[index] = value;
        this._redrawTexts(index);
    }

    get color() { return this._color; }
    set color(value) { this._color = value; }
    setColor(color) {
        this._color = color;
        this._redrawTexts();
    }

    get textColor() { return this._textColor; }
    set textColor(value) { this._textColor = value; }
    setTextColor(color) {
        this._textColor = color;
        this._redrawTexts();
    }

    get edgeColor() { return this._edgeColor; }
    set edgeColor(value) { this._edgeColor = value; }
    setEdgeColor(color) {
        this._edgeColor = color;
        this._redrawTexts();
    }

    get font() { return this._font; }
    set font(value) { this._font = value; }
    setFont(font) {
        this._font = font;
        this._redrawTexts();
    }

    get fontSize() { return this._fontSize; }
    set fontSize(value) { this._fontSize = value; }
    setFontSize(fontSize) {
        this._fontSize = fontSize;
        this._redrawTexts();
    }

    _build(canvasSize) {
        const sides = this.sides;
        const indicesUv = this.indicesUv !== undefined ? this.indicesUv : new Array(sides).fill(0);
        const flipOrders = this.indicesUv !== undefined ? this.flipOrder : [false];
        const uvs = this.indicesUv !== undefined ? this.attrUv : [this.attrUv];
        const normals = this.normals;
        const editable = this.editable !== undefined ? this.editable : new Array(sides).fill(true);

        this._materials.length = sides;
        this._geometries.length = sides;
        this._texts.length = sides;


        let sideIndex = 0;
        this.indices.forEach((indices, index) => {
            const indexUv = indicesUv[index];
            const attrUv = uvs[indexUv];
            const normal = normals[index];
            const flipOrder = flipOrders[indexUv];
            const vertices = indices.flat().map(i => this.vertices[i]);
            const { geometry, material } = this.createTriangle(canvasSize, flipOrder, vertices, attrUv, normal);

            this._materials[index] = material;
            this._geometries[index] = geometry;
            this._texts[index] = editable[index] ? `Face '${++sideIndex}'` : "";
        });
    }

    createTriangle(canvasSize, flipOrder, vertices, attrUv, normal) {
        const attrIndices = this.attrIndices;

        const material = MaterialBuilder.build({
            side: THREE.FrontSide,
            envMap: AssetLibrary.envLight.activeMap,
            map: new ProceduralCanvasTexture(canvasSize, flipOrder)
        });

        const geometry = new THREE.BufferGeometry();
        const attrPosition = new THREE.Float32BufferAttribute(vertices.map(v => v.toArray()).flat(), 3);

        if (attrIndices) geometry.setIndex(attrIndices);
        if (attrUv) geometry.addAttribute("uv", attrUv);
        geometry.addAttribute("position", attrPosition);
        geometry.computeVertexNormals();

        return { geometry, material };
    }
}

export default GeometryBase;
export { GeometryBase };